import { useState } from "react";
import { DefaultButton } from "../../../../../common/components/button/defaultButton/DefaultButton";
import { showCustomConfirmDialog } from "../../../../../common/components/dialogs/AppConfirmDialog";
import { copyToClipboard } from "../../../../../common/utilities/clipboardUtility";
import { getSuccessToast } from "../../../../../common/infrastructure/utilities/toastUtility";
import { useStudioQrCodes } from "./hooks/useStudioQrCodes";
import { IStudioQrCode } from "./models/studioQrCodeModels";
import { IStudioQrCodeFormModel } from "./models/studioQrCodeFormModel";
import { StudioQrCodeDialog } from "./StudioQrCodeDialog";
import { TransferQrCodeDialog } from "./TransferQrCodeDialog";
import { QrCodePreview } from "../../../../../common/components/qrCode/QrCodePreview";
import styles from "./StudioQrCodesSection.module.scss";

interface IStudioQrCodesSectionProps {
  studioId: number;
}

export const StudioQrCodesSection = ({
  studioId,
}: IStudioQrCodesSectionProps) => {
  const {
    qrCodes,
    isLoading,
    isSaving,
    isTransferring,
    saveQrCode,
    deleteQrCode,
    transferQrCode,
  } = useStudioQrCodes(studioId);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<IStudioQrCode | null>(null);

  const [transferDialogVisible, setTransferDialogVisible] = useState(false);
  const [transferring, setTransferring] = useState<IStudioQrCode | null>(
    null,
  );

  const openCreate = () => {
    setEditing(null);
    setDialogVisible(true);
  };

  const openEdit = (qrCode: IStudioQrCode) => {
    setEditing(qrCode);
    setDialogVisible(true);
  };

  const handleSave = (values: IStudioQrCodeFormModel) => {
    saveQrCode(values, () => setDialogVisible(false));
  };

  const openTransfer = (qrCode: IStudioQrCode) => {
    setTransferring(qrCode);
    setTransferDialogVisible(true);
  };

  const handleTransfer = (targetStudioId: number) => {
    if (!transferring) {
      return;
    }

    transferQrCode(transferring.id, targetStudioId, () =>
      setTransferDialogVisible(false),
    );
  };

  const handleCopy = async (redirectUrl: string) => {
    const ok = await copyToClipboard(redirectUrl);
    if (ok) {
      getSuccessToast("Адресът беше копиран.");
    }
  };

  const confirmDelete = (qrCode: IStudioQrCode) => {
    const warning =
      qrCode.scanCount > 0
        ? `<br/><strong>Внимание:</strong> този код е сканиран ${qrCode.scanCount} пъти. Ако е отпечатан някъде, ще спре да работи.`
        : "";

    showCustomConfirmDialog(
      `Сигурни ли сте, че искате да премахнете „${qrCode.name}“?${warning}`,
      () => deleteQrCode(qrCode.id),
    );
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <p className={styles.header_hint}>
          Генерирайте QR кодове, които препращат към избран от вас адрес.
          Дестинацията може да се сменя по всяко време — отпечатаният код
          остава същият и продължава да работи.
        </p>
        <DefaultButton label="Нов QR код" width="auto" onClick={openCreate} />
      </div>

      {isLoading && qrCodes.length === 0 ? (
        <p className={styles.empty}>Зареждане…</p>
      ) : qrCodes.length === 0 ? (
        <p className={styles.empty}>Още няма създадени QR кодове.</p>
      ) : (
        <div className={styles.list}>
          {qrCodes.map((qrCode) => (
            <div key={qrCode.id} className={styles.card}>
              <QrCodePreview value={qrCode.redirectUrl} name={qrCode.name} />

              <div className={styles.card_details}>
                <div className={styles.card_title}>
                  <span className={styles.card_name}>{qrCode.name}</span>
                  <span
                    className={
                      qrCode.isActive
                        ? styles.badge_active
                        : styles.badge_inactive
                    }
                  >
                    {qrCode.isActive ? "Активен" : "Неактивен"}
                  </span>
                </div>

                <button
                  type="button"
                  className={styles.card_link}
                  title="Копирай адреса"
                  onClick={() => handleCopy(qrCode.redirectUrl)}
                >
                  {qrCode.redirectUrl}
                </button>

                <div className={styles.card_row}>
                  <span className={styles.card_label}>Дестинация:</span>
                  <span className={styles.card_value}>
                    {qrCode.destinationUrl}
                  </span>
                </div>

                <div className={styles.card_row}>
                  <span className={styles.card_label}>Сканирания:</span>
                  <span className={styles.card_value}>{qrCode.scanCount}</span>
                </div>

                <div className={styles.card_actions}>
                  <DefaultButton
                    label="Редакция"
                    design="outline"
                    width="auto"
                    onClick={() => openEdit(qrCode)}
                  />
                  <DefaultButton
                    label="Прехвърляне"
                    design="outline"
                    width="auto"
                    onClick={() => openTransfer(qrCode)}
                  />
                  <DefaultButton
                    label="Изтрий"
                    design="link"
                    width="auto"
                    onClick={() => confirmDelete(qrCode)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <StudioQrCodeDialog
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        editing={editing}
        isSaving={isSaving}
        onSave={handleSave}
      />

      <TransferQrCodeDialog
        visible={transferDialogVisible}
        onHide={() => setTransferDialogVisible(false)}
        qrCode={transferring}
        currentStudioId={studioId}
        isTransferring={isTransferring}
        onTransfer={handleTransfer}
      />
    </div>
  );
};
